import { useEffect, useState } from "react";
import { Smartphone, X, Download, Sparkles } from "lucide-react";

export default function AddToHomeBanner() {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem("bd_a2hs_dismissed");
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone;
    if (isStandalone) return; // already installed, hide forever
    if (!dismissed) setVisible(true);

    // Capture Android/Chrome/Edge install prompt for one-tap install
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Hide banner once installed
    const onInstalled = () => {
      localStorage.setItem("bd_a2hs_dismissed", "1");
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible) return null;

  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const dismiss = () => {
    localStorage.setItem("bd_a2hs_dismissed", "1");
    setVisible(false);
  };

  const handleInstallClick = async () => {
    // Android/Chrome/Edge: one-tap native install
    if (deferredPrompt) {
      setInstalling(true);
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice && choice.outcome === "accepted") {
          localStorage.setItem("bd_a2hs_dismissed", "1");
          setVisible(false);
        }
      } catch (_) {
        // fall through to manual modal
      } finally {
        setInstalling(false);
        setDeferredPrompt(null);
      }
      return;
    }
    // iOS or any browser that doesn't support beforeinstallprompt: show manual steps
    setOpen(true);
  };

  const canOneTap = !!deferredPrompt && !isIOS;
  const ctaLabel = canOneTap ? "Install" : "Show me how";
  const CtaIcon = canOneTap ? Download : Smartphone;

  return (
    <>
      <div
        className="card-clay p-4 mb-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 flex items-center gap-3"
        data-testid="a2hs-banner"
      >
        <div className="w-10 h-10 rounded-2xl bg-green-500 border-b-4 border-green-700 flex items-center justify-center flex-shrink-0">
          <CtaIcon className="text-white" strokeWidth={2.5} size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-fredoka font-bold text-slate-900 flex items-center gap-2 flex-wrap">
            <span>📱 Save Bag Dash Kids to your home screen</span>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-400 text-amber-950 border-b-2 border-amber-600 leading-none"
              data-testid="a2hs-bonus-badge"
              title="Earn +1 Dash Point when you install"
            >
              <Sparkles size={10} strokeWidth={3} />
              +1 Dash Point
            </span>
          </div>
          <div className="text-xs text-slate-600">
            Open like a real app · faster booking · streak notifications.
          </div>
        </div>
        <button
          onClick={handleInstallClick}
          disabled={installing}
          className="btn-push-green px-3 py-2 text-xs whitespace-nowrap disabled:opacity-60"
          data-testid="a2hs-install-btn"
        >
          {installing ? "Installing…" : ctaLabel}
        </button>
        <button
          onClick={dismiss}
          className="p-1.5 rounded-xl hover:bg-green-100"
          aria-label="Dismiss"
          data-testid="a2hs-dismiss"
        >
          <X size={16} />
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="a2hs-modal"
        >
          <div
            className="card-clay p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-fredoka text-2xl font-bold">
                Add to Home Screen
              </h2>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-xl hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>
            <div
              className="mb-4 px-3 py-2 rounded-2xl bg-amber-50 border border-amber-200 flex items-center gap-2 text-sm text-amber-900"
              data-testid="a2hs-modal-bonus"
            >
              <Sparkles size={16} strokeWidth={2.5} className="text-amber-600 flex-shrink-0" />
              <span><b>Bonus:</b> Get <b>+1 Dash Point</b> the first time you open the installed app.</span>
            </div>
            {isIOS ? (
              <ol className="space-y-3 text-sm text-slate-700">
                <li className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-green-100 text-green-800 font-bold flex items-center justify-center flex-shrink-0">
                    1
                  </span>
                  <span>
                    Tap the <b>Share</b> button (the square with the up arrow ⬆️) at the bottom of Safari.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-green-100 text-green-800 font-bold flex items-center justify-center flex-shrink-0">
                    2
                  </span>
                  <span>
                    Scroll down and tap <b>"Add to Home Screen"</b>.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-green-100 text-green-800 font-bold flex items-center justify-center flex-shrink-0">
                    3
                  </span>
                  <span>
                    Tap <b>"Add"</b> in the top-right. Done! 🎉
                  </span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-slate-700">
                <li className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-green-100 text-green-800 font-bold flex items-center justify-center flex-shrink-0">
                    1
                  </span>
                  <span>Open the browser menu (the ⋮ icon, usually top-right).</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-green-100 text-green-800 font-bold flex items-center justify-center flex-shrink-0">
                    2
                  </span>
                  <span>
                    Tap <b>"Add to Home screen"</b> or <b>"Install app"</b>.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-green-100 text-green-800 font-bold flex items-center justify-center flex-shrink-0">
                    3
                  </span>
                  <span>
                    Confirm by tapping <b>"Add"</b> or <b>"Install"</b>. Done! 🎉
                  </span>
                </li>
              </ol>
            )}
            <button
              onClick={() => setOpen(false)}
              className="btn-push-green w-full mt-5 py-2.5"
              data-testid="a2hs-close"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
